/**
 * Phase STYLE-YOURSELF-RESULT-PARITY
 *
 * Shared header for Style Yourself combinator + result surfaces.
 * Visual parity with the main Results page header:
 *   - Top eyebrow (LIKE2FIT · …) — uppercase, tracked
 *   - Serif italic title beneath
 *   - 3-column grid: left back chevron / center title block / right action
 *
 * If `rightAction` is undefined, a 36×36 spacer is rendered in its place
 * so the center column stays optically centered (matches the result page's
 * loading-state behavior — the share icon is hidden until ready).
 */
import React from "react";

type Props = {
  eyebrow: string;
  title: string;
  onBack?: () => void;
  rightAction?: React.ReactNode;
};

const ICON_BOX = 36;

export function StyleYourselfHeader({
  eyebrow,
  title,
  onBack,
  rightAction,
}: Props) {
  return (
    <div
      className="grid items-center px-4 py-3"
      style={{ gridTemplateColumns: `${ICON_BOX}px 1fr ${ICON_BOX}px` }}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: ICON_BOX,
            height: ICON_BOX,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 22,
            lineHeight: 1,
            color: "#0A0A0A",
          }}
        >
          ‹
        </button>
      ) : (
        <span style={{ width: ICON_BOX, height: ICON_BOX }} />
      )}

      <div className="flex flex-col items-center text-center">
        <span
          style={{
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(10,10,10,0.55)",
          }}
        >
          {eyebrow}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display), "Instrument Serif", serif',
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: 22,
            lineHeight: 1.1,
            color: "#0A0A0A",
            marginTop: 2,
          }}
        >
          {title}
        </span>
      </div>

      {rightAction !== undefined ? (
        <div
          style={{
            width: ICON_BOX,
            height: ICON_BOX,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {rightAction}
        </div>
      ) : (
        <span style={{ width: ICON_BOX, height: ICON_BOX }} />
      )}
    </div>
  );
}
